import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Trash2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

export const AttachmentAdminDialog = ({ open, onOpenChange }) => {
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/admin/uploads`, { withCredentials: true });
      setFiles(res.data?.files || []);
    } catch { setFiles([]); toast.error('Could not load uploaded files'); }
  };

  useEffect(() => { if (open) { setFiles(null); load(); } }, [open]);

  const remove = async (f) => {
    const warn = f.usage_count > 0
      ? `"${f.filename}" is used in ${f.usage_count} place${f.usage_count === 1 ? '' : 's'} — removing it will break those attachments. Remove anyway?`
      : `Remove "${f.filename}"?`;
    if (!window.confirm(warn)) return;
    setBusy(f.path);
    try {
      await axios.delete(`${API_URL}/api/rfp/admin/uploads/${f.path}`, { withCredentials: true });
      toast.success(`"${f.filename}" removed`);
      setFiles(prev => prev.filter(x => x.path !== f.path));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
    finally { setBusy(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfp-file-admin-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Uploaded files — usage &amp; cleanup</DialogTitle>
        </DialogHeader>
        {files === null ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF] mx-auto my-8" />
        ) : files.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8" data-testid="rfp-file-admin-empty">No uploaded files.</p>
        ) : (
          <div className="space-y-1.5">
            {files.map((f, i) => (
              <div key={f.path} className="flex items-center gap-3 border border-slate-100 rounded-md px-2.5 py-1.5"
                data-testid={`rfp-file-admin-row-${i}`}>
                {(f.content_type || '').startsWith('image/') ? (
                  <img src={`${API_URL}/api/rfp/files/${f.path}`} alt={f.filename} className="h-9 w-12 object-cover rounded border border-slate-200 shrink-0" />
                ) : (
                  <span className="h-9 w-12 flex items-center justify-center bg-slate-50 rounded border border-slate-100 shrink-0">
                    <FileText className="w-4 h-4 text-slate-300" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-[#0F2040] truncate">{f.filename}</p>
                  <p className="text-[9px] text-slate-400 truncate">{fmtSize(f.size)} · {f.uploaded_by} · {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</p>
                </div>
                <Badge variant="outline" className={`text-[9px] shrink-0 ${f.usage_count > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                  data-testid={`rfp-file-admin-usage-${i}`}>
                  {f.usage_count} use{f.usage_count === 1 ? '' : 's'}
                </Badge>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-400 hover:text-red-600 shrink-0"
                  disabled={busy === f.path} onClick={() => remove(f)} title="Remove file"
                  data-testid={`rfp-file-admin-remove-${i}`}>
                  {busy === f.path ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
